import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { MessageSquare, Send, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn, openExternalUrl } from "@/lib/utils";
import { logError } from "@/lib/logger";
import { useToast } from "@/hooks/use-toast";
import { useFindigoChatAccess } from "@/hooks/useFindigoChatAccess";
import { useFindigoChatData } from "@/hooks/useFindigoChatData";
import { getQuickQuestions, parseIntent } from "@/lib/findigoChat/intentEngine";
import { buildChatResponse } from "@/lib/findigoChat/responseBuilder";
import type { ChatAction } from "@/lib/findigoChat/types";
import { useLocation } from "react-router-dom";
import { useFindigoChatContext } from "./FindigoChatProvider";

interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  text: string;
  actions?: ChatAction[];
}

const FindigoChatWidgetInner = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { currentJob } = useFindigoChatContext();
  const {
    profileSummary,
    applications,
    applicationsLoading,
    matches,
    matchesLoading,
    refreshMatches,
    hasCV,
    pendingAttemptsCount,
  } = useFindigoChatData();

  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const quickQuestions = useMemo(() => getQuickQuestions(), []);
  const hasCurrentJob = Boolean(currentJob?.jobId);
  const visibleQuestions = useMemo(
    () =>
      quickQuestions.filter((question) => {
        if (["applied_status", "why_this_job", "can_apply"].includes(question.intent)) {
          return hasCurrentJob;
        }
        return true;
      }),
    [quickQuestions, hasCurrentJob],
  );

  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
    const id = requestAnimationFrame(() => {
      const el = messagesEndRef.current;
      try {
        if (el && typeof (el as HTMLElement).scrollIntoView === "function") {
          (el as HTMLElement).scrollIntoView({ behavior: "smooth", block: "end" });
        }
      } catch {
        // jsdom or older envs may not support scrollIntoView
      }
    });
    return () => cancelAnimationFrame(id);
  }, [messages, open]);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open]);

  const sendMessage = (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;

    let response: { message: string; actions: ChatAction[] };
    try {
      const intent = parseIntent(trimmed);
      response = buildChatResponse(intent, {
        currentJob,
        applications,
        applicationsLoading,
        matches,
        matchesLoading,
        profile: profileSummary,
        hasCV,
        pendingAttemptsCount,
      });
      if (!response?.message?.trim()) {
        response = {
          message: "Jag kunde inte svara på det. Prova en av knapparna nedan.",
          actions: response?.actions ?? [],
        };
      }
    } catch (err) {
      logError("Findigo chat sendMessage failed", err);
      toast({
        title: "Något gick fel",
        description: "Försök igen eller välj en fråga nedan.",
        variant: "destructive",
      });
      response = {
        message: "Något gick fel. Försök igen eller välj en av frågorna nedan.",
        actions: [],
      };
    }

    setMessages((prev) => [
      ...prev,
      { id: `${Date.now()}-user`, role: "user", text: trimmed },
      {
        id: `${Date.now()}-assistant`,
        role: "assistant",
        text: response.message,
        actions: response.actions,
      },
    ]);
    setInput("");
  };

  const clearHistory = () => {
    setMessages([]);
  };

  const handleAction = async (action: ChatAction) => {
    try {
      if (action.type === "navigate" && action.href) {
        navigate(action.href);
        setOpen(false);
        return;
      }

      if (action.type === "refetch_matches") {
        await refreshMatches();
        toast({ title: "Matchning uppdateras" });
        return;
      }

      if (action.type === "open_external" && action.url) {
        if (!openExternalUrl(action.url)) {
          toast({
            title: "Ogiltig länk",
            description: "Kunde inte öppna annonsen.",
            variant: "destructive",
          });
        }
      }
    } catch (err) {
      logError("Findigo chat handleAction failed", err);
      toast({
        title: "Något gick fel",
        description: "Försök igen.",
        variant: "destructive",
      });
    }
  };

  /* P0: Chat is overlay only. Fixed + portal so it never affects document flow or layout width. */
  return (
    <div
      data-testid="findigo-chat-portal-root"
      className="findigo-chat-portal fixed right-4 z-[9999] flex flex-col items-end gap-3 bottom-[calc(env(safe-area-inset-bottom,0px)+1rem)]"
      style={{ pointerEvents: "none" } as React.CSSProperties}
    >
      <div className="pointer-events-auto" style={{ maxWidth: "min(100vw - 2rem, var(--chat-widget-width-lg))" }}>
      {open && (
        <div
          data-testid="findigo-chat-panel"
          className="w-[var(--chat-widget-width)] sm:w-[var(--chat-widget-width-lg)] max-h-[70vh] min-h-[280px] rounded-2xl border border-border bg-card shadow-xl flex flex-col overflow-hidden"
        >
          <div className="flex items-center justify-between border-b border-border px-4 py-3 shrink-0">
            <div>
              <div className="text-sm font-semibold text-foreground">Fråga Findigo</div>
              <div className="text-xs text-muted-foreground">
                {currentJob?.title ? `Aktiv annons: ${currentJob.title}` : "Ingen jobbannons vald"}
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={clearHistory}
                disabled={messages.length === 0}
                className="min-h-[var(--touch-target)] min-w-[var(--touch-target)]"
                aria-label="Rensa chatt"
              >
                Rensa
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="min-h-[var(--touch-target)] min-w-[var(--touch-target)]"
                onClick={() => setOpen(false)}
                aria-label="Stäng chatt"
              >
                <X className="h-4 w-4" aria-hidden />
              </Button>
            </div>
          </div>

          <div
            className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-4 py-3"
            role="region"
            aria-label="Chatmeddelanden"
          >
            <div className="flex flex-col gap-3">
              {messages.length === 0 && (
                <div className="rounded-lg bg-muted/60 p-3 text-xs text-muted-foreground">
                  Ställ en fråga eller välj ett förslag nedan. Jag svarar kort och länkar vidare.
                </div>
              )}
              {messages.map((message) => (
                <div key={message.id} className="flex flex-col gap-2">
                  <div
                    data-testid="findigo-chat-message"
                    data-role={message.role}
                    className={cn(
                      "max-w-[85%] rounded-xl px-3 py-2 text-sm whitespace-pre-line",
                      message.role === "user"
                        ? "self-end bg-primary text-primary-foreground"
                        : "self-start bg-muted text-foreground",
                    )}
                  >
                    {message.text}
                  </div>
                  {message.role === "assistant" && message.actions && (
                    <div className="flex flex-wrap gap-2">
                      {message.actions.map((action) => (
                        <Button
                          key={action.id}
                          type="button"
                          variant="outline"
                          size="sm"
                          className="min-h-[var(--touch-target)]"
                          onClick={() => handleAction(action)}
                          data-testid="findigo-chat-action"
                          aria-label={action.label}
                        >
                          {action.label}
                        </Button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <div ref={messagesEndRef} aria-hidden="true" />
            </div>
          </div>

          <div className="border-t border-border px-4 py-3 space-y-3 shrink-0">
            {!hasCurrentJob && (
              <div className="text-xs text-muted-foreground">
                Välj ett jobb i listan för jobbspecifika frågor.
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              {visibleQuestions.map((question) => (
                <Button
                  key={question.id}
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="text-xs min-h-[var(--touch-target)]"
                  onClick={() => sendMessage(question.label)}
                  data-testid="findigo-chat-chip"
                  aria-label={question.label}
                >
                  {question.label}
                </Button>
              ))}
            </div>

            <div className="flex items-center gap-2 shrink-0">
              <Input
                ref={inputRef}
                value={input}
                onChange={(event) => setInput(event.target.value)}
                placeholder="Skriv din fråga..."
                className="min-h-[var(--touch-target)]"
                aria-label="Skriv din fråga till Findigo"
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !event.shiftKey) {
                    event.preventDefault();
                    sendMessage(input);
                  }
                }}
              />
              <Button
                type="button"
                size="icon"
                className="min-h-[var(--touch-target)] min-w-[var(--touch-target)]"
                onClick={() => sendMessage(input)}
                aria-label="Skicka meddelande"
                disabled={!input.trim()}
              >
                <Send className="h-4 w-4" aria-hidden />
              </Button>
            </div>
          </div>
        </div>
      )}

      <Button
        type="button"
        data-testid="findigo-chat-launcher"
        onClick={() => setOpen((prev) => !prev)}
        className="rounded-full shadow-lg gap-2 min-h-[var(--touch-target)] min-w-[var(--touch-target)] p-3 md:px-4"
        aria-label={open ? "Stäng chatt" : "Öppna chatt - Fråga Findigo"}
        aria-expanded={open}
      >
        <MessageSquare className="h-5 w-5 shrink-0" aria-hidden />
        <span className="hidden md:inline">Fråga Findigo</span>
      </Button>
      </div>
    </div>
  );
};

const ONBOARDING_ROUTES = ["/auth", "/onboarding", "/create-profile", "/reset-password"];

/**
 * P0 UX: Chat must never affect layout (LinkedIn/Apple/Stripe rule).
 * Rendered via portal to document.body so it is never in the app's flex/grid flow.
 * Position fixed + high z-index = overlay only; main content keeps full width.
 */
export const FindigoChatWidget = () => {
  const location = useLocation();
  const { allowed, loading } = useFindigoChatAccess();

  if (ONBOARDING_ROUTES.includes(location.pathname)) return null;
  if (loading || !allowed) return null;

  if (typeof document === "undefined" || !document.body) return null;
  return createPortal(<FindigoChatWidgetInner />, document.body);
};
